import { useState, useCallback } from 'react';
import { ProcessService } from '../services/processService';
import { NestedStateMachine, ExecuteNestedStateMachineResponse } from '../types/process';

export const useNestedStateMachine = (processId: string) => {
  const [nestedStateMachine, setNestedStateMachine] = useState<NestedStateMachine | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executionResult, setExecutionResult] = useState<ExecuteNestedStateMachineResponse | null>(
    null
  );

  const processService = ProcessService.getInstance();

  const loadNestedStateMachine = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await processService.getNestedStateMachine(processId);
      setNestedStateMachine(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load nested state machine');
    } finally {
      setIsLoading(false);
    }
  }, [processId, processService]);

  const validateNestedStateMachine = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const errors = await processService.validateNestedStateMachine(processId);
      setValidationErrors(errors);
      return errors.length === 0;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate nested state machine');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [processId, processService]);

  const executeNestedStateMachine = useCallback(
    async (currentState: string, parameters?: Record<string, unknown>) => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await processService.executeNestedStateMachine(
          processId,
          currentState,
          parameters
        );
        setExecutionResult(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to execute nested state machine');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [processId, processService]
  );

  const reset = useCallback(() => {
    setNestedStateMachine(null);
    setValidationErrors([]);
    setError(null);
    setExecutionResult(null);
  }, []);

  return {
    nestedStateMachine,
    validationErrors,
    isLoading,
    error,
    executionResult,
    loadNestedStateMachine,
    validateNestedStateMachine,
    executeNestedStateMachine,
    reset,
  };
};
